import React, { useMemo } from 'react'

const slugifyKey = (str = '', max = 32) => {
  return String(str)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, max || 32)
}

const canonicalKey = (key) => {
  const k = key || {}
  const obj = Object.keys(k).sort().reduce((acc, pid) => { acc[pid] = k[pid]; return acc }, {})
  return JSON.stringify(obj)
}

const cartesian = (arrays) => arrays.reduce((acc, curr) => acc.flatMap(a => curr.map(c => [...a, c])), [[]])

export default function VariantEditor({ variantProps, skus, onChange, marginPricing = false }) {
  const propsOrdered = useMemo(() => {
    const arr = Array.isArray(variantProps) ? [...variantProps] : []
    arr.sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
    return arr
  }, [variantProps])

  const handlePropChange = (idx, patch) => {
    const next = [...(variantProps || [])]
    next[idx] = { ...(next[idx] || {}), ...patch }
    // normalize displayOrder to index
    const normalized = next.map((p, i) => ({ ...p, displayOrder: i }))
    onChange && onChange({ variantProps: normalized, skus })
  }

  const addProperty = () => {
    const baseName = 'Property'
    const id = slugifyKey(baseName + '-' + ((variantProps?.length || 0) + 1))
    const next = [...(variantProps || []), { id, name: baseName, type: 'text', values: [], displayOrder: (variantProps?.length || 0) }]
    onChange && onChange({ variantProps: next, skus })
  }

  const removeProperty = (idx) => {
    const next = (variantProps || []).filter((_, i) => i !== idx).map((p, i) => ({ ...p, displayOrder: i }))
    // Remove corresponding keys from SKUs
    const nextSkus = (skus || []).map((s) => {
      const k = { ...(s?.key || {}) }
      const propId = (variantProps || [])[idx]?.id
      if (propId) delete k[propId]
      return { ...s, key: k }
    })
    onChange && onChange({ variantProps: next, skus: nextSkus })
  }

  const moveProperty = (idx, dir) => {
    const next = [...(variantProps || [])]
    const j = idx + dir
    if (j < 0 || j >= next.length) return
    const tmp = next[idx]
    next[idx] = next[j]
    next[j] = tmp
    const normalized = next.map((p, i) => ({ ...p, displayOrder: i }))
    onChange && onChange({ variantProps: normalized, skus })
  }

  const addValue = (pidx) => {
    const prop = (variantProps || [])[pidx]
    const values = Array.isArray(prop?.values) ? [...prop.values] : []
    const baseName = 'Option'
    const id = slugifyKey(baseName + '-' + (values.length + 1))
    values.push({ id, name: baseName })
    handlePropChange(pidx, { values })
  }

  const removeValue = (pidx, vidx) => {
    const prop = (variantProps || [])[pidx]
    const values = (prop?.values || []).filter((_, i) => i !== vidx)
    handlePropChange(pidx, { values })
  }

  const updateValue = (pidx, vidx, patch) => {
    const prop = (variantProps || [])[pidx]
    const values = [...(prop?.values || [])]
    values[vidx] = { ...(values[vidx] || {}), ...patch }
    handlePropChange(pidx, { values })
  }

  const generateSkus = () => {
    const propsArr = propsOrdered
    if (propsArr.length === 0) {
      onChange && onChange({ variantProps, skus: [] })
      return
    }
    const valueIdLists = propsArr.map(p => (p.values || []).map(v => ({ propId: p.id, valueId: v.id })))
    const combos = cartesian(valueIdLists)
    const existing = new Map((skus || []).map(s => [canonicalKey(s.key), s]))
    const nextSkus = combos.map((combo) => {
      const key = {}
      combo.forEach(({ propId, valueId }) => { key[propId] = valueId })
      const can = canonicalKey(key)
      const prev = existing.get(can)
      return prev ? { ...prev, key } : { key, countInStock: 0, unavailable: false }
    })
    onChange && onChange({ variantProps, skus: nextSkus })
  }

  const updateSku = (idx, patch) => {
    const next = [...(skus || [])]
    next[idx] = { ...(next[idx] || {}), ...patch }
    onChange && onChange({ variantProps, skus: next })
  }

  return (
    <div>
      <div className='d-flex align-items-center mb-2' style={{ gap: 8 }}>
        <strong>Variant Properties</strong>
        <button type='button' className='btn btn-sm btn-outline-primary' onClick={addProperty}>Add Property</button>
      </div>
      {(propsOrdered || []).map((prop, idx) => (
        <div key={prop.id} className='border rounded p-2 mb-3'>
          <div className='d-flex align-items-center' style={{ gap: 8 }}>
            <div style={{ minWidth: 220 }}>
              <label className='form-label mb-1'>Name</label>
              <input className='form-control' value={prop.name || ''} onChange={(e) => handlePropChange(idx, { name: e.target.value })} />
            </div>
            <div style={{ minWidth: 220 }}>
              <label className='form-label mb-1'>Key (id)</label>
              <input className='form-control' value={prop.id || ''} onChange={(e) => handlePropChange(idx, { id: e.target.value })} onBlur={(e) => { if (!e.target.value.trim()) handlePropChange(idx, { id: slugifyKey(prop.name || 'property') }) }} />
            </div>
            <div style={{ minWidth: 140 }}>
              <label className='form-label mb-1'>Type</label>
              <select className='form-control' value={prop.type || 'text'} onChange={(e) => handlePropChange(idx, { type: e.target.value })}>
                <option value='text'>Text</option>
                <option value='image'>Image</option>
              </select>
            </div>
            <div className='ms-auto d-flex align-items-end' style={{ gap: 6 }}>
              <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => moveProperty(idx, -1)} disabled={idx === 0}>Up</button>
              <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => moveProperty(idx, +1)} disabled={idx === propsOrdered.length - 1}>Down</button>
              <button type='button' className='btn btn-sm btn-outline-danger' onClick={() => removeProperty(idx)}>Remove</button>
            </div>
          </div>

          <div className='mt-3'>
            <div className='d-flex align-items-center mb-2' style={{ gap: 8 }}>
              <strong>Values</strong>
              <button type='button' className='btn btn-sm btn-outline-primary' onClick={() => addValue(idx)}>Add Value</button>
            </div>
            {(prop.values || []).map((val, vidx) => (
              <div key={val.id || vidx} className='d-flex align-items-end mb-2' style={{ gap: 8 }}>
                <div style={{ minWidth: 220 }}>
                  <label className='form-label mb-1'>Name</label>
                  <input className='form-control' value={val.name || ''} onChange={(e) => updateValue(idx, vidx, { name: e.target.value })} />
                </div>
                <div style={{ minWidth: 220 }}>
                  <label className='form-label mb-1'>Id</label>
                  <input className='form-control' value={val.id || ''} onChange={(e) => updateValue(idx, vidx, { id: e.target.value })} onBlur={(e) => { if (!e.target.value.trim()) updateValue(idx, vidx, { id: slugifyKey(val.name || 'value') }) }} />
                </div>
                {prop.type === 'image' && (
                  <div style={{ minWidth: 280, flex: 1 }}>
                    <label className='form-label mb-1'>Image URL</label>
                    <input className='form-control' value={val.image || ''} onChange={(e) => updateValue(idx, vidx, { image: e.target.value })} />
                  </div>
                )}
                <div className='form-check ms-2'>
                  <input className='form-check-input' type='checkbox' id={`hot-${idx}-${vidx}`} checked={!!val.hot} onChange={(e) => updateValue(idx, vidx, { hot: e.target.checked })} />
                  <label className='form-check-label' htmlFor={`hot-${idx}-${vidx}`}>Hot</label>
                </div>
                <div className='form-check ms-2'>
                  <input className='form-check-input' type='checkbox' id={`dis-${idx}-${vidx}`} checked={!!val.disabled} onChange={(e) => updateValue(idx, vidx, { disabled: e.target.checked })} />
                  <label className='form-check-label' htmlFor={`dis-${idx}-${vidx}`}>Disabled</label>
                </div>
                <button type='button' className='btn btn-sm btn-outline-danger ms-2' onClick={() => removeValue(idx, vidx)}>Remove</button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className='d-flex align-items-center mt-3' style={{ gap: 8 }}>
        <strong>SKUs</strong>
        <button type='button' className='btn btn-sm btn-outline-primary' onClick={generateSkus}>Generate SKUs</button>
      </div>
      {(skus || []).length === 0 ? (
        <div className='text-muted mt-2'>No SKUs. Click Generate SKUs to create combinations.</div>
      ) : (
        <div className='mt-2'>
          {(skus || []).map((s, idx) => {
            const keyDesc = propsOrdered.map(p => {
              const vid = s?.key?.[p.id]
              const val = (p.values || []).find(v => v.id === vid)
              return `${p.name}: ${val ? val.name : vid || '-'}`
            }).join(' · ')
            return (
              <div key={idx} className='border rounded p-2 mb-2'>
                <div className='mb-2'><strong>{keyDesc}</strong></div>
                <div className='d-flex align-items-end' style={{ gap: 8 }}>
                  <div style={{ minWidth: 140 }}>
                    <label className='form-label mb-1'>Stock</label>
                    <input type='number' min='0' className='form-control' value={s.countInStock ?? 0} onChange={(e) => updateSku(idx, { countInStock: Math.max(0, Number(e.target.value || 0)) })} />
                  </div>
                  <div hidden={marginPricing} style={{ minWidth: 140 }}>
                    <label className='form-label mb-1'>Price (override)</label>
                    <input readOnly={marginPricing} type='number' min='0' step='0.01' className='form-control' value={s.price ?? ''} onChange={(e) => { const v = e.target.value; updateSku(idx, { price: v === '' ? undefined : Number(v) }) }} />
                  </div>
                  <div style={{ minWidth: 280, flex: 1 }}>
                    <label className='form-label mb-1'>Image (optional)</label>
                    <input className='form-control' value={s.image || ''} onChange={(e) => updateSku(idx, { image: e.target.value })} />
                  </div>
                  <div className='form-check ms-2'>
                    <input className='form-check-input' type='checkbox' id={`sku-unavail-${idx}`} checked={!!s.unavailable} onChange={(e) => updateSku(idx, { unavailable: e.target.checked })} />
                    <label className='form-check-label' htmlFor={`sku-unavail-${idx}`}>Unavailable</label>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
